// Standalone-mode auth. When merged into apps/customer-app, replace this
// provider with the host `AuthContext` — the shape of `user` is compatible.
// See docs/FRONTEND_INTEGRATION.md → "Auth wiring".

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthResponse, User } from "@coremonit/types";
import { api, setToken } from "../api/client";

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

const SESSION_KEY = "cm.microservices.user";

function readCachedUser(): User | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch { return null; }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(readCachedUser);
  const [loading, setLoading] = useState(!user);

  useEffect(() => {
    if (user) { setLoading(false); return; }
    api.get<{ user: User }>("/auth/me")
      .then((r) => {
        setUser(r.user);
        try { localStorage.setItem(SESSION_KEY, JSON.stringify(r.user)); } catch { /* ignore */ }
      })
      .catch(() => { /* not authenticated */ })
      .finally(() => setLoading(false));
  }, [user]);

  const login = useCallback(async (email: string, password: string) => {
    const r = await api.post<AuthResponse>("/auth/login", { email, password });
    setToken(r.token);
    setUser(r.user);
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(r.user)); } catch { /* ignore */ }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  }, []);

  const value = useMemo<AuthState>(() => ({ user, loading, login, logout }), [user, loading, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
